import { ApiClientError, apiFetch } from '@/lib/apiClient';
import type { UserRole } from '@coreltb/shared/constants';
import { useQuery, useQueryClient } from '@tanstack/react-query';

export interface AuthUser {
  id: string;
  email: string;
  name: string;
  /** Effective role po impersonate (jezeli admin/dev wcielony — to bedzie wcielana rola). */
  role: UserRole;
  /** Prawdziwa rola z bazy. Frontend pokazuje "Wciel sie!" w topbarze gdy actualRole=admin/developer. */
  actualRole: UserRole;
  isActive: boolean;
}

/** Team-domain CF Access logout URL — czysci sesje w calym CF Access tenant. */
const CF_ACCESS_TEAM_DOMAIN = 'coreltb.cloudflareaccess.com';

/**
 * Hook do pobierania zalogowanego usera z /auth/me.
 *
 * Retry policy:
 * - 401 (no JWT) → AuthGuard pokazuje LoginRequiredScreen, BEZ retry (nadpisanie globalu)
 * - 403 USER_NOT_ACTIVE → PendingActivationScreen, BEZ retry
 * - Inne (network errors, 5xx) → uzywa globalnego retry (2 proby z backoff)
 *
 * Logout:
 * - czysci wszystkie TanStack Query cache (krytyczne — inaczej user widzi
 *   zcacheowane dane mimo ze sesja CF Access wygasla)
 * - redirect na team-domain logout z returnTo na nasza app
 *   (CF Access invaliduje cookie, redirectuje z powrotem na /
 *   gdzie /auth/me 401 → LoginRequiredScreen)
 */
export function useAuth() {
  const qc = useQueryClient();

  const query = useQuery<AuthUser, ApiClientError>({
    queryKey: ['auth', 'me'],
    queryFn: () => apiFetch<AuthUser>('/auth/me'),
    staleTime: 5 * 60 * 1000,
    retry: (failureCount, error) => {
      // 401/403 — nie ma sensu retry, AuthGuard obsluzy
      if (error instanceof ApiClientError) {
        if (error.status === 401 || error.status === 403) return false;
      }
      // Network/5xx — proboj 2x (z backoff od TanStack)
      return failureCount < 2;
    },
  });

  return {
    user: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    /**
     * Soft logout — pokazuje brand-owned LoggedOutScreen.
     *
     * Uwaga: cala app.coreltb.pl jest za CF Access, wiec NIE da sie zrobic
     * full team-domain logout + redirect z powrotem do publicznego ekranu
     * (CF Access zablokowalby request bez sesji i pokazal swoj OTP form
     * zanim SPA sie zaladuje).
     *
     * Wiec robimy "soft logout":
     * - czyscimy TanStack Query cache (wszystkie dane usera znikaja z UI)
     * - ustawiamy sessionStorage flag — AuthGuard widzi go i pokazuje
     *   LoggedOutScreen zamiast normalnego dashboardu
     * - reload zeby AuthGuard sie przelaczyl
     *
     * Cookie CF Access pozostaje (24h TTL). Pelne wylogowanie z CF Access
     * jest dostepne jako secondary action w LoggedOutScreen ("Wyloguj
     * calkowicie z Cloudflare Access" → team-domain logout).
     */
    logout: () => {
      try {
        window.sessionStorage.setItem('coreltb.justLoggedOut', '1');
      } catch {
        // sessionStorage disabled (private mode) — flow zadziala bez flagu
        // (user trafi do dashboardu, a my nie pokazemy LoggedOutScreen)
      }
      qc.clear();
      window.location.assign('/');
    },
  };
}

/** URL pelnego wylogowania z CF Access (team-domain). Uzywane jako secondary action. */
export const CF_ACCESS_FULL_LOGOUT_URL = `https://${CF_ACCESS_TEAM_DOMAIN}/cdn-cgi/access/logout`;

/**
 * Sprawdza czy uzytkownik wlasnie sie wylogowal (po returnTo z CF Access logout).
 * Czyta flag z sessionStorage. Wywolywane przez AuthGuard.
 *
 * NIE konsumuje flagu (musi przetrwac aby ekran nie znikl po klikach na inne strony
 * przed kliknieciem "Zaloguj ponownie"). Flag czysci sie dopiero przy kliku w "Zaloguj".
 */
export function isJustLoggedOut(): boolean {
  try {
    return window.sessionStorage.getItem('coreltb.justLoggedOut') === '1';
  } catch {
    return false;
  }
}

export function clearLoggedOutFlag(): void {
  try {
    window.sessionStorage.removeItem('coreltb.justLoggedOut');
  } catch {
    // ignore
  }
}
